
import Vue, { PropType } from 'vue';

import Table from '@/components/common/Table.vue';
import AssignDeviceModal from '@/components/modal/AssignDeviceModal.vue';
import UnassignDeviceModal from '@/components/modal/UnassignDeviceModal.vue';
import DeviceEditModal from '@/components/modal/DeviceEditModal.vue';
import { LuminaireRow } from '@/interfaces/luminaires';
import { Controller } from '@/interfaces/controllers';
import { Device, OverrideInfoLuminaire, Unassign } from '@/interfaces/locations';
import { DeviceType } from '@/interfaces/types';
import { Alert } from '@/interfaces/alerts';
import { BvTableFieldArray } from 'bootstrap-vue';

interface ComponentData {
	selectedController: Controller | null;
	selectedLuminaire: Device | null;
	unassignableDevice: LuminaireRow | null;
	assignableLuminaire: boolean;
}

interface LuminaireTableRow {
	id: number;
	name: string;
	type: string;
	description: string;
	alert: string[];
	power: number | string;
	identify: boolean;
	off: boolean;
	unassign: Unassign;
}

export default Vue.extend({
	components: {
		Table,
		AssignDeviceModal,
		UnassignDeviceModal,
		DeviceEditModal,
	},

	props: {
		controllers: { type: Array as PropType<Controller[]>, default: (): Controller[] => [] },
		luminaires: {
			type: Array as PropType<OverrideInfoLuminaire[]>,
			default: (): OverrideInfoLuminaire[] => [],
		},
		deviceTypes: { type: Array as PropType<DeviceType[]>, default: (): DeviceType[] => [] },
		activeAlerts: { type: Array as PropType<Alert[]>, default: (): Alert[] => [] },
		unassignedLuminaires: { type: Array as PropType<Device[]>, default: (): Device[] => [] },
		userPermission: { type: Boolean, default: true },
		zoneName: { type: String },
		roomSensors: { type: Array as PropType<string[]>, default: (): string[] => [] },
	},

	data(): ComponentData {
		return {
			selectedController: null,
			selectedLuminaire: null,
			unassignableDevice: null,
			assignableLuminaire: false,
		};
	},

	computed: {
		fields(): BvTableFieldArray {
			return [
				{ key: 'name', label: this.$t('Name').toString(), sortable: true },
				{ key: 'type', label: this.$t('Type').toString(), sortable: true },
				{ key: 'description', label: this.$t('Description').toString(), sortable: false },
				{ key: 'alert', label: this.$t('Alerts').toString() },
				{ key: 'power', label: this.$t('Power').toString(), sortable: true },
				{ key: 'badgeOverride', label: this.$t('Override').toString() },
				{ key: 'offset', label: this.$i18n.t('offset').toString(), sortable: true },
				{ key: 'delete', label: '' },
			];
		},

		luminaireFilter(): number[] {
			return this.deviceTypes
				.filter((deviceType: DeviceType) => deviceType.features.includes('dt_light_control'))
				.map((deviceType: DeviceType) => deviceType.id);
		},

		luminaireRows(): LuminaireTableRow[] {
			// We really don't want to add sensors to luminaires list.
			// Compare with SensorList.vue procedure sensorRows() row about 169-176
			return this.luminaires
				.filter(
					(luminaire: OverrideInfoLuminaire) =>
						luminaire.fk_device_type && this.luminaireFilter.includes(luminaire.fk_device_type)
				)
				.map((luminaire: OverrideInfoLuminaire) => {
					const deviceType = this.deviceTypes.find(
						(deviceType: DeviceType) => deviceType.id === luminaire.fk_device_type
					);
					const controller =
						this.controllers.find((c) => c.id === luminaire.fk_controller) ?? undefined;
					return {
						id: luminaire.id,
						name:
							controller && controller.guid
								? `${luminaire.name} - ${controller.guid.slice(controller.guid.length - 4)}`
								: luminaire.name,
						type: deviceType?.name ?? this.$t('Unknown').toString(),
						description: luminaire.description,
						power: deviceType?.rated_power ?? '',
						alert: this.activeAlerts
							.filter((activeAlert: Alert) => {
								return activeAlert.fk_device === luminaire.id;
							})
							.map((activeAlert: Alert) => {
								let answer = String(this.$t(activeAlert.fk_alert_type));
								if (activeAlert.description.length > 0) {
									answer = `${answer} : ${activeAlert.description}`;
									if (
										['1', '2', '3', '256', '257', '258', '259', '512'].includes(
											activeAlert.description
										)
									) {
										answer = `${answer} - ${this.$t(
											String(`light_control_error_${activeAlert.description}`)
										).toString()}`;
									}
								}
								return answer;
							}),
						identify: luminaire.identifyActive,
						off: luminaire.offActive,
						offset: luminaire.light_level_offset,
						unassign: this.allowUnassign(luminaire.id) ? 'on' : 'disabled',
					};
				});
		},
	},

	methods: {
		allowUnassign(luminaireId: number): boolean {
			let selectedLuminaire: Device = this.luminaires.find((l) => l.id === luminaireId)!;
			if (selectedLuminaire!.fk_controller !== null) {
				const selcont: Controller | null =
					this.controllers.find((c) => c.id === selectedLuminaire!.fk_controller) ?? null;
				if (selcont) {
					return !this.roomSensors?.includes(selcont.guid);
				}
			}
			return true;
		},

		assignDevice(luminaireId: number): void {
			this.$emit('assignDevice', luminaireId);
		},

		unassignDevice(luminaireId: number): void {
			this.$emit('unassignDevice', luminaireId);
		},

		async assignDeviceModal() {
			this.assignableLuminaire = true;
			await this.$nextTick();
			this.$bvModal.show('modal-assign-device');
		},

		async unassignDeviceModal(luminaireRow: LuminaireRow) {
			if (this.allowUnassign(luminaireRow.id)) {
				this.unassignableDevice = luminaireRow;
			}
			await this.$nextTick();
			this.$bvModal.show('modal-unassign-device');
		},

		async onLuminaireRowClick(row: LuminaireRow) {
			this.selectedLuminaire = this.luminaires.find((l) => l.id === row.id)!;
			if (this.selectedLuminaire!.fk_controller !== null) {
				this.selectedController =
					this.controllers.find((c) => c.id === this.selectedLuminaire!.fk_controller) ?? null;
			}
			await this.$nextTick();
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.deviceEditModal as any).show();
		},

		cleanModalData(): void {
			this.assignableLuminaire = false;
			this.unassignableDevice = null;
			this.selectedLuminaire = null;
			this.selectedController = null;
		},

		onDeviceModify(luminaire: Partial<Device>) {
			this.$emit('modifyDevice', luminaire, this.selectedLuminaire);
		},
	},
});
